import { api } from '../utils/apiClient';
import { usePolling } from './usePolling';
import { config } from '../config';
import { useRef } from 'react';

/**
 * Custom hook for fetching and polling a user's token wallet balance
 * 
 * This hook uses polling with the global interval to automatically fetch and refresh wallet balance
 * data for a user. It will only perform the API calls when the user is logged in (has email and token).
 * 
 * @param email - The user's email address
 * @param token - The user's authentication token
 * @returns Object containing the wallet balance data and status
 */
export function useWalletBalance(email: string | null, token: string | null) {
  // Store the last known good balance value for use when the API fails
  const lastKnownBalance = useRef<number | null>(null);
  
  const { data: balance, loading, error, lastUpdate, refetch } = usePolling<number>({
    fetchFn: async () => {
      // Skip API call if no email or token
      if (!email || !token) {
        lastKnownBalance.current = null;
        return 0;
      }
      
      try {
        const response = await api.wallet.getTokenBalance();
      
        if (response.success && response.tokenBalance !== undefined) {
          // Store the successful balance for future use
          lastKnownBalance.current = response.tokenBalance;
          return response.tokenBalance;
        }
        
        // If API call fails but we have a previous value, use it
        if (lastKnownBalance.current !== null) {
          return lastKnownBalance.current;
        }
        
        // As a last resort return 0
        return 0;
      } catch (error) {
        // If we have a previous balance, use it
        if (lastKnownBalance.current !== null) {
          return lastKnownBalance.current;
        }
        
        // As a last resort return 0
        return 0;
      }
    },
    initialData: 0,
    interval: config.polling.global,
    dependencies: [email, token],
    enabled: !!email && !!token, // Only poll when user is logged in
    silentUpdates: true // Prevent UI flickering during updates
  });

  return {
    balance,
    loading,
    error,
    lastUpdate,
    refetch,
    // Add a flag to indicate if the server is unavailable but we're showing cached data
    isUsingCachedBalance: lastKnownBalance.current !== null && error !== null
  };
} 